/**
 * The CORTEX API error envelope.
 *
 * Mirrors `apps/api/src/middleware/errorHandler.ts`. The list of codes
 * here is the **closed** set — adding a new code requires updating both
 * sides in the same PR, which is the whole point of co-locating the
 * schema in `@cortex/domain`.
 */

import { z } from 'zod';

export const ErrorCodeSchema = z.enum([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'unprocessable_entity',
  'rate_limited',
  'internal_error',
  'request_failed',
  // ─── facility-overlay rejections (PR #4c) ─────────────────────────
  /** The quote's product is not within the broker's facility scope. */
  'product_not_in_facility',
  /** A category uses a risk class the broker's facility does not cover. */
  'risk_class_not_in_facility',
  /**
   * The authenticated broker's brokerage has no active FacilityAgreement
   * — quotes cannot be produced until Lombard activates one.
   */
  'no_active_facility',
  // ─── admin entity CRUD rejections (PR #5a.1) ──────────────────────
  /** The supplied entityTypeSlug does not match a known EntityType. */
  'entity_type_not_found',
  /** A different entity already uses this name (`entities_name_unique`). */
  'entity_name_taken',
  // ─── admin user CRUD rejections (PR #5a.3) ────────────────────────
  /** Another user already uses this email (`users_email_unique`). */
  'user_email_taken',
  // ─── admin role assignment rejections (PR #5a.4) ──────────────────
  /** The role's entity type does not match the user's entity type. */
  'role_entity_type_mismatch',
  /** Assigning this role would violate a Segregation-of-Duties rule. */
  'role_sod_conflict',
  /** The user already holds this role. */
  'role_already_assigned',
  /** Removing this role would leave the system with no AdminSuperUser. */
  'role_last_admin',
  // ─── bind authority rejections (PR #5c) ──────────────────────────
  /**
   * The gross premium exceeds the caller's effective bind authority limit
   * (facility `bindLimitB` for brokers, role/override limit for internal users).
   */
  'bind_authority_exceeded',
  /** The caller has no bind authority at all (null limit or `canBind = false` on role). */
  'bind_no_authority',
  /** The broker's facility agreement does not permit binding (`canBind = false`). */
  'bind_cannotbind_facility',
  // ─── admin facility CRUD rejections (PR #5b) ──────────────────────
  /** `valid_from` is not strictly before `valid_to`. */
  'facility_date_range_invalid',
  /** `brokerage_min_pct` is greater than `brokerage_max_pct`. */
  'facility_brokerage_range_invalid',
  /** `product_scope` must contain at least one product slug. */
  'facility_no_products',
  /** `allowed_risk_classes` must contain at least one class. */
  'facility_no_risk_classes',
  /** `bind_limit_b` is required when `can_bind = true`. */
  'facility_bind_limit_required',
  /** The target entity does not exist or is not a brokerage-type entity. */
  'facility_entity_invalid',
  // ─── referral flow rejections (PR #5d) ────────────────────────────
  /**
   * An open referral for this (user, quoteRef) pair already exists.
   * The broker must wait for the current referral to be resolved.
   */
  'referral_duplicate_open',
  /** No referral found with the supplied ID. */
  'referral_not_found',
  /** The referral has already been approved or declined and cannot be acted on again. */
  'referral_already_resolved',
  // ─── quote persistence (PR #5e) ───────────────────────────────────────
  /** No quote found with the supplied ref. */
  'quote_not_found',
]);

export const ApiErrorSchema = z.object({
  code: ErrorCodeSchema,
  message: z.string().min(1),
  requestId: z.string().min(1),
});

export const ErrorEnvelopeSchema = z.object({
  error: ApiErrorSchema,
});

export type ErrorCode = z.infer<typeof ErrorCodeSchema>;
export type ApiError = z.infer<typeof ApiErrorSchema>;
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;
